import type * as React from 'react';
import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Bell, ChevronLeft, ChevronRight } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';

import { Avatar, AvatarFallback, AvatarImage } from '@dfq/shared/components/ui/avatar';
import { Button } from '@dfq/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@dfq/shared/components/ui/dropdown-menu';
import { cn } from '@dfq/shared/lib/utils';

/**
 * 56px admin top bar rendered above the main content area of the Console.
 *
 * Layout mirrors Figma `23:36`:
 * - Left: breadcrumb trail (e.g. `Admin / Orders / WRP-2024`). Intermediate
 *   items with a `to` prop render as `react-router-dom` links; the last
 *   breadcrumb entry always renders as plain text (current-page indicator).
 * - Right: notification bell (stub — icon + unread-count placeholder slot,
 *   full popover dropdown arrives in SH-NOTIFY-002) and an avatar button
 *   wrapping shadcn `DropdownMenu` with Profile / Sign out actions.
 *
 * Tokens used: `bg-page` (background), `border-divider` (bottom border),
 * `text-text-primary` (current crumb), `text-text-secondary` (intermediate
 * crumbs and the `/` separators).
 */
const adminTopBarVariants = cva(
  'flex h-14 w-full items-center justify-between border-b border-divider bg-page px-8',
  {
    variants: {
      /**
       * Reserved for future spacing variations (e.g. a denser bar used by
       * embedded admin panels). The default matches the Figma 56px height.
       */
      density: {
        default: '',
      },
    },
    defaultVariants: { density: 'default' },
  },
);

export interface AdminBreadcrumbItem {
  /** Visible label (e.g. "Admin", "Orders", "WRP-2024"). */
  label: string;
  /**
   * Destination for intermediate crumbs. Omit (or leave undefined) on the
   * final item — the last breadcrumb always renders as plain text even if a
   * `to` is provided, so current-page indicators stay semantically correct.
   */
  to?: string;
}

export interface AdminTopBarUser {
  /** Full display name, also used to derive the avatar fallback initials. */
  name: string;
  /** Optional avatar image URL; falls back to initials when absent. */
  avatarUrl?: string;
}

export interface AdminTopBarProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'children'>,
    VariantProps<typeof adminTopBarVariants> {
  /**
   * Breadcrumb trail rendered left-aligned. The last item is always rendered
   * as plain text (no link) regardless of whether `to` is supplied.
   */
  breadcrumb: AdminBreadcrumbItem[];
  /** User identity rendered in the avatar cluster on the right. */
  user: AdminTopBarUser;
  /**
   * Count of unread notifications. When `> 0`, a small badge is layered on
   * the bell icon. The bell itself is a stub until SH-NOTIFY-002 wires the
   * real dropdown — clicking it is a no-op unless `onNotificationsClick`
   * is supplied.
   *
   * Ignored when `notificationsSlot` is provided — the supplied slot owns
   * its own bell + badge rendering.
   */
  unreadNotifications?: number;
  /** Click handler for the notification bell stub. Ignored when `notificationsSlot` is provided. */
  onNotificationsClick?: () => void;
  /**
   * SH-NOTIFY-002 — when supplied, replaces the built-in bell stub on the
   * right cluster. Containers wire the live `<NotificationBell>` capsule
   * (with Redux + polling + navigation) and pass it here. Keeps the top
   * bar capsule pure: it knows nothing about the slice or its thunks.
   */
  notificationsSlot?: React.ReactNode;
  /** Called when the user activates "Sign out" in the avatar dropdown. */
  onSignOut?: () => void;
  /** Called when the user activates "Profile" in the avatar dropdown. */
  onProfile?: () => void;
  /**
   * When provided, a leading collapse/expand icon button renders to the
   * LEFT of the breadcrumb. The parent owns the expanded/collapsed
   * sidebar state; the top bar is a dumb button surface. Omit to hide
   * the toggle (e.g. on viewports where the sidebar is forced-collapsed).
   */
  onSidebarToggle?: () => void;
  /**
   * Reflects the parent's current sidebar state so the toggle chooses the
   * right chevron direction and `aria-label`. Only read when
   * `onSidebarToggle` is supplied.
   */
  sidebarCollapsed?: boolean;
}

/**
 * Derive up-to-two-letter initials from a display name for the avatar
 * fallback (`"Jane Doe"` → `"JD"`, `"Cher"` → `"C"`).
 */
function deriveInitials(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length === 0) return '?';
  const parts = trimmed.split(/\s+/u).filter(Boolean);
  if (parts.length === 1) return parts[0]!.charAt(0).toUpperCase();
  return `${parts[0]!.charAt(0)}${parts[parts.length - 1]!.charAt(0)}`.toUpperCase();
}

export function AdminTopBar({
  breadcrumb,
  user,
  unreadNotifications = 0,
  onNotificationsClick,
  notificationsSlot,
  onSignOut,
  onProfile,
  onSidebarToggle,
  sidebarCollapsed = false,
  density,
  className,
  ...rest
}: AdminTopBarProps) {
  const initials = deriveInitials(user.name);
  const hasUnread = unreadNotifications > 0;
  const unreadLabel = unreadNotifications > 99 ? '99+' : String(unreadNotifications);

  return (
    <header
      className={cn(adminTopBarVariants({ density }), className)}
      {...rest}
    >
      <div className="flex min-w-0 items-center gap-2">
        {onSidebarToggle ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={
              sidebarCollapsed ? 'Expand navigation sidebar' : 'Collapse navigation sidebar'
            }
            aria-pressed={sidebarCollapsed}
            onClick={onSidebarToggle}
            className="shrink-0"
          >
            {sidebarCollapsed ? (
              <ChevronRight aria-hidden="true" />
            ) : (
              <ChevronLeft aria-hidden="true" />
            )}
          </Button>
        ) : null}
      <nav aria-label="Breadcrumb" className="min-w-0">
        <ol className="flex items-center gap-2 text-ui-breadcrumb">
          {breadcrumb.map((crumb, index) => {
            const isLast = index === breadcrumb.length - 1;
            return (
              <Fragment key={`${crumb.label}-${index}`}>
                <li className="min-w-0 truncate">
                  {isLast || !crumb.to ? (
                    <span
                      className={cn(
                        'truncate',
                        isLast ? 'text-text-primary' : 'text-text-secondary',
                      )}
                      aria-current={isLast ? 'page' : undefined}
                    >
                      {crumb.label}
                    </span>
                  ) : (
                    <Link
                      to={crumb.to}
                      className="truncate text-text-secondary hover:text-text-primary hover:underline"
                    >
                      {crumb.label}
                    </Link>
                  )}
                </li>
                {!isLast ? (
                  <li
                    aria-hidden="true"
                    className="text-text-secondary select-none"
                  >
                    /
                  </li>
                ) : null}
              </Fragment>
            );
          })}
        </ol>
      </nav>
      </div>

      <div className="flex items-center gap-2">
        {notificationsSlot !== undefined ? (
          notificationsSlot
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={
              hasUnread
                ? `Notifications, ${unreadLabel} unread`
                : 'Notifications'
            }
            className="relative"
            onClick={onNotificationsClick}
          >
            <Bell aria-hidden="true" />
            {hasUnread ? (
              <span
                data-testid="admin-topbar-unread-count"
                className="absolute top-1 right-1 inline-flex min-w-4 items-center justify-center rounded-full bg-accent-destructive px-1 text-[10px] font-semibold leading-none text-on-primary"
              >
                {unreadLabel}
              </span>
            ) : null}
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Open user menu for ${user.name}`}
              className="rounded-full"
            >
              <Avatar className="size-8">
                {user.avatarUrl ? (
                  <AvatarImage src={user.avatarUrl} alt={user.name} />
                ) : null}
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-44">
            <div className="px-2 py-1.5 text-sm font-medium text-text-primary">
              {user.name}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onProfile}>Profile</DropdownMenuItem>
            <DropdownMenuItem onSelect={onSignOut}>Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
